import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isAutoGlobalCard} from '../../../../helpers/models/cardHelpers';
import {CardModel} from '../../../../models/api/cardModel';

/*
 * Props.
 */

interface ContactCardFooterAddToContactsActionProps {
  card?: CardModel;
  onAddToPrivateContactsAsync: AsyncHandler;
  onAddToCompanyContactsAsync: AsyncHandler;
}

/*
 * Style.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  align-items: center;
`;

const StyledDropdownIcon = styled(Icon)`
  margin-right: 8px;
`;

/*
 * Component.
 */

export const ContactCardFooterAddToContactsAction: FC<ContactCardFooterAddToContactsActionProps> = (
  props,
) => {
  const theme = useUserTheme();
  const {card, onAddToPrivateContactsAsync, onAddToCompanyContactsAsync} = props;

  if (shouldHideAddToContactsButton(card)) {
    return null;
  }
  return (
    <PopoverCoordinator
      renderButton={(hasHover) => (
        <IconButton
          hasHover={hasHover}
          iconName="newContact"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          placement="top"
          label={<Trans id="contact-card-footer-action-add-to-contacts">Add to contacts</Trans>}
        />
      )}
      renderModal={(popoverProps) =>
        renderDropdown(popoverProps, onAddToPrivateContactsAsync, onAddToCompanyContactsAsync)
      }
    />
  );
};

/*
 * Helpers.
 */

/**
 * We should hide the option if we:
 *   - Have a card AND
 *   - The card is not an auto card (already added as a company or private contact)
 */
function shouldHideAddToContactsButton(card?: CardModel) {
  return card && !isAutoGlobalCard(card);
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  onAddToPrivateContactsAsync: AsyncHandler,
  onAddToCompanyContactsAsync: AsyncHandler,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Dropdown {...popoverProps}>
          <DropdownList>
            <DropdownItem onSelect={onAddToCompanyContactsAsync}>
              <StyledDropdownItemContent>
                <StyledDropdownIcon name="buildingFilledSmall" size={16} color={theme.palette.teal.shade40} />
                <Trans id="contact-card-footer-action-dropdown-add-to-company-contacts">
                  Add to Company contacts
                </Trans>
              </StyledDropdownItemContent>
            </DropdownItem>
            <DropdownItem onSelect={onAddToPrivateContactsAsync}>
              <StyledDropdownItemContent>
                <StyledDropdownIcon name="lockSmall" size={16} color={theme.greys.shade70} />
                <Trans id="contact-card-footer-action-dropdown-add-to-private-contacts">
                  Add to Private contacts
                </Trans>
              </StyledDropdownItemContent>
            </DropdownItem>
          </DropdownList>
        </Dropdown>
      )}
    </UserThemeConsumer>
  );
}
